import { useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import type { DataItem, ProcessedItem, WorkerInMessage, WorkerOutMessage } from './types';

const WebWorkers = () => {
    const [status, setStatus] = useState<'idle' | 'processing' | 'paused' | 'done' | 'error'>('idle');
    const [progress, setProgress] = useState(0);
    const [results, setResults] = useState<ProcessedItem[]>([]);
    const [error, setError] = useState<string | null>(null);
    const workerRef = useRef<Worker | null>(null);

    const startProcessing = useCallback(() => {
        // Clear previous results
        setResults([]);
        setError(null);
        setProgress(0);
        setStatus('processing');

        // Generate some sample data
        const data: DataItem[] = Array.from({ length: 10000 }, (_, i) => ({
            id: i,
            value: `Item ${i}`
        }));

        // Create a new worker
        const worker = new Worker(new URL('./worker.ts', import.meta.url), {
            type: 'module',
        });
        workerRef.current = worker;

        worker.onmessage = (e: MessageEvent<WorkerOutMessage>) => {
            const msg = e.data;
            switch (msg.type) {
                case 'progress':
                    setProgress(msg.value);
                    break;
                case 'done':
                    setResults(msg.results);
                    setProgress(100);
                    setStatus('done');
                    worker.terminate();
                    workerRef.current = null;
                    break;
                case 'error':
                    setError(msg.message);
                    setStatus('error');
                    worker.terminate();
                    workerRef.current = null;
                    break;
            }
        };

        worker.onerror = (e) => {
            setError(e.message);
            setStatus('error');
            worker.terminate();
            workerRef.current = null;
        };

        const message: WorkerInMessage = { type: 'start', data, chunkSize: 500 };
        worker.postMessage(message);
    }, []);

    const pauseProcessing = useCallback(() => {
        if (workerRef.current && status === 'processing') {
            workerRef.current.postMessage({ type: 'pause' });
            setStatus('paused');
        }
    }, [status]);

    const resumeProcessing = useCallback(() => {
        if (workerRef.current && status === 'paused') {
            workerRef.current.postMessage({ type: 'resume' });
            setStatus('processing');
        }
    }, [status]);

    const stopProcessing = useCallback(() => {
        if (workerRef.current) {
            workerRef.current.terminate();
            workerRef.current = null;
            setStatus('idle');
            setProgress(0);
        }
    }, []);

    return (
        <main className="flex flex-col justify-start items-center min-h-screen py-8 p-[32px]">
            <h1 className="text-3xl font-bold mb-8">Web Workers</h1>

            <section className="w-full max-w-2xl mb-12 p-6 bg-blue-50 border border-blue-200 rounded-lg shadow-sm">
                <h2 className="text-xl font-semibold text-blue-900 mb-4">Objective: Offload Heavy Computations to a Web Worker</h2>
                <p className="text-blue-800 mb-4">
                    Demonstrate how to keep the UI responsive while performing CPU-intensive tasks using Web Workers.
                </p>
                <div className="space-y-2">
                    <p className="font-medium text-blue-900">Key Features:</p>
                    <ul className="list-disc list-inside text-blue-800 space-y-1 ml-2">
                        <li>Processing data in chunks to report progress</li>
                        <li>Maintaining a responsive UI during computation</li>
                        <li>Worker communication via postMessage API (Start/Pause/Resume)</li>
                        <li>Handling worker termination and errors</li>
                    </ul>
                </div>
            </section>

            <div className="w-full max-w-2xl flex flex-col gap-6">
                <div className="flex gap-4 items-center">
                    {(status === 'idle' || status === 'done' || status === 'error') && (
                        <button
                            onClick={startProcessing}
                            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                        >
                            Start Heavy Task
                        </button>
                    )}

                    {status === 'processing' && (
                        <button
                            onClick={pauseProcessing}
                            className="bg-yellow-500 text-white px-6 py-2 rounded-lg hover:bg-yellow-600 transition-colors font-medium"
                        >
                            Pause
                        </button>
                    )}

                    {status === 'paused' && (
                        <button
                            onClick={resumeProcessing}
                            className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium"
                        >
                            Resume
                        </button>
                    )}

                    {(status === 'processing' || status === 'paused') && (
                        <button
                            onClick={stopProcessing}
                            className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors font-medium"
                        >
                            Stop
                        </button>
                    )}
                </div>

                {status !== 'idle' && (
                    <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden shadow-inner">
                        <div
                            className={`h-full bg-blue-600 transition-all duration-300 ease-out`}
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                )}

                <div className="bg-white p-6 border border-gray-200 rounded-lg shadow-sm min-h-[200px]">
                    <h3 className="text-lg font-semibold mb-4">Status: <span className="capitalize">{status}</span></h3>

                    {error && (
                        <div className="p-4 bg-red-50 text-red-700 border border-red-200 rounded-md">
                            Error: {error}
                        </div>
                    )}

                    {status === 'processing' && results.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-10 text-gray-500">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
                            <p>Worker is crunching data...</p>
                        </div>
                    )}
                </div>
            </div>

            <Link to="/" className="mt-8 text-blue-600 hover:text-blue-800 transition-colors font-medium flex items-center gap-2">
                ← Back to Home
            </Link>
        </main>
    );
};

export default WebWorkers;
